import { TotpMultiFactorGenerator, type MultiFactorInfo, type MultiFactorResolver } from 'firebase/auth';

// El resolver de segundo factor no es serializable: se guarda en memoria entre LoginPage y TotpVerifyPage.
let pendingResolver: MultiFactorResolver | null = null;

export function setPendingResolver(resolver: MultiFactorResolver | null) {
  pendingResolver = resolver;
}

export function takePendingResolver(): MultiFactorResolver | null {
  return pendingResolver;
}

/** El factor TOTP de la cuenta, o null si inició sesión con otro segundo factor. */
export function totpHint(resolver: MultiFactorResolver): MultiFactorInfo | null {
  return resolver.hints.find((h) => h.factorId === TotpMultiFactorGenerator.FACTOR_ID) ?? null;
}

/** Completa el inicio de sesión con el código de 6 dígitos (lo usan /login/verificar y la invitación). */
export async function resolveTotpSignIn(resolver: MultiFactorResolver, hint: MultiFactorInfo, code: string) {
  const assertion = TotpMultiFactorGenerator.assertionForSignIn(hint.uid, code.replace(/\s/g, ''));
  await resolver.resolveSignIn(assertion);
}

export const NO_TOTP_FACTOR =
  'Tu cuenta no tiene un segundo factor TOTP configurado. Contacta al Owner de tu organización.';

export const MFA_ENROLLED_NOTICE =
  'Segundo factor activado. Entra de nuevo con tu contraseña y el código de 6 dígitos de tu app.';

/** Mensajes en español para los códigos de error de Identity Platform. */
export function authErrorMessage(code: string | undefined): string {
  switch (code) {
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
    case 'auth/user-not-found':
    case 'auth/invalid-email':
      return 'El email o la contraseña no son correctos.';
    case 'auth/too-many-requests':
      return 'Demasiados intentos. Espera unos minutos e intenta de nuevo.';
    case 'auth/invalid-verification-code':
      return 'El código no es válido. Revisa tu app de autenticación e intenta de nuevo.';
    case 'auth/requires-recent-login':
      return 'Por seguridad, vuelve a iniciar sesión antes de configurar el segundo factor.';
    case 'auth/unverified-email':
      return 'Verifica tu email antes de configurar el segundo factor.';
    case 'auth/network-request-failed':
      return 'No pudimos conectarnos. Revisa tu conexión.';
    case 'auth/totp-challenge-timeout':
    case 'auth/code-expired':
      return 'El código expiró. Espera el siguiente código de tu app e intenta de nuevo.';
    case 'auth/user-token-expired':
    case 'auth/user-disabled':
      return 'Tu sesión se cerró. Vuelve a iniciar sesión.';
    case 'auth/second-factor-already-in-use':
      return 'Esta cuenta ya tiene el segundo factor activado. Inicia sesión con tu código.';
    case 'auth/maximum-second-factor-count-exceeded':
      return 'La cuenta alcanzó el máximo de segundos factores. Contacta al operador.';
    case 'auth/unsupported-first-factor':
      return 'Tu forma de inicio de sesión no admite segundo factor. Contacta al operador.';
    default:
      return code
        ? `No pudimos completar la operación (${code}). Intenta de nuevo.`
        : 'No pudimos completar la operación. Intenta de nuevo.';
  }
}
